/**
 * SuperAdmin → Rechazos de Hacienda. Todos los comprobantes rechazados, con error o sin
 * respuesta de todas las cuentas: por qué fue (explicado), cómo se corrige, quién lo
 * resuelve, y el seguimiento (en revisión, cliente avisado, resuelto). Cuando el
 * comprobante termina aceptado, el caso desaparece solo.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  AlertTriangle, RefreshCw, CheckCircle2, Search, LogIn, Eye, MessageSquare, BellRing, XCircle, Wrench, UserCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import { panelApi, tenantsApi } from '../utils/api';
import {
  Encabezado, Boton, Cargando, Aviso, Vacio, Kpi, Modal, Campo, inputCls, fFechaHora, fMonto, hace
} from '../components/ui';

const SEGUIMIENTO = {
  nuevo: { label: 'Nuevo', cls: 'bg-red-50 text-red-700 border-red-200' },
  revisando: { label: 'En revisión', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  resuelto: { label: 'Resuelto', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  descartado: { label: 'Descartado', cls: 'bg-gray-100 text-gray-600 border-gray-200' }
};
const HACIENDA = { rechazado: 'Rechazado', error: 'Error de envío', pendiente: 'Pendiente', enviado: 'Enviado', procesando: 'Procesando' };

export default function Rechazos() {
  const [params] = useSearchParams();
  const [casos, setCasos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [verCerrados, setVerCerrados] = useState(false);
  const [cuenta, setCuenta] = useState(params.get('cuenta') || '');
  const [categoria, setCategoria] = useState('');
  const [estado, setEstado] = useState('');
  const [buscar, setBuscar] = useState('');
  const [abierto, setAbierto] = useState(null);
  const [nota, setNota] = useState(null); // caso al que se le escribe nota
  const [textoNota, setTextoNota] = useState('');
  const [mensaje, setMensaje] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const [inicio, setInicio] = useState(null);       // fecha de inicio de operaciones
  const [editInicio, setEditInicio] = useState(null);

  const cargar = useCallback(async (silencioso = false) => {
    silencioso ? setRefrescando(true) : setCargando(true);
    try {
      const r = await panelApi.rechazos({ dias: 365, cerrados: verCerrados ? 1 : undefined });
      setCasos(r.data.data || []);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudieron cargar los rechazos' });
    } finally {
      setCargando(false);
      setRefrescando(false);
    }
  }, [verCerrados]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { panelApi.inicioOperaciones().then(r => setInicio(r.data.fecha)).catch(() => {}); }, []);

  const guardarInicio = async () => {
    try {
      const r = await panelApi.guardarInicioOperaciones(editInicio);
      setInicio(r.data.fecha);
      setEditInicio(null);
      setAviso({ tipo: 'ok', texto: 'Fecha de inicio guardada' });
      cargar(true);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo guardar la fecha' });
    }
  };
  const fechaCorta = (f) => { if (!f) return ''; const [y, m, d] = f.split('-'); return `${d}/${m}/${y}`; };

  const validos = casos.filter(c => !c.errorLectura);
  const cuentas = useMemo(() => [...new Map(validos.map(c => [c.cuentaId, c.cuenta])).entries()], [casos]); // eslint-disable-line
  const categorias = useMemo(() => [...new Map(validos.map(c => [c.categoria.id, c.categoria.titulo])).entries()], [casos]); // eslint-disable-line

  const lista = validos.filter(c =>
    (!cuenta || String(c.cuentaId) === String(cuenta)) &&
    (!categoria || c.categoria.id === categoria) &&
    (!estado || c.seguimiento.estado === estado) &&
    (!buscar || [c.consecutivo, c.cliente, c.empresa, c.mensaje].some(v => String(v || '').toLowerCase().includes(buscar.toLowerCase())))
  );

  const cuenta_ = (estadoSeg) => validos.filter(c => c.seguimiento.estado === estadoSeg).length;
  const deSoporte = validos.filter(c => c.categoria.quien === 'soporte' && !['resuelto', 'descartado'].includes(c.seguimiento.estado)).length;

  const actualizar = async (c, cambios, ok) => {
    setOcupado(`${c.cuentaId}:${c.documentoId}`);
    try {
      await panelApi.seguimiento(c.cuentaId, c.documentoId, cambios);
      setAviso({ tipo: 'ok', texto: ok });
      await cargar(true);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo actualizar el caso' });
    } finally {
      setOcupado(null);
    }
  };

  const entrar = async (c) => {
    setOcupado(`entrar:${c.cuentaId}:${c.documentoId}`);
    try {
      const r = await tenantsApi.impersonate(c.cuentaId);
      const { token, tenant_slug } = r.data;
      window.open(`/impersonar?token=${encodeURIComponent(token)}&slug=${encodeURIComponent(tenant_slug)}&ir=${encodeURIComponent('/facturacion/historial')}`, '_blank');
      if (c.seguimiento.estado === 'nuevo') await panelApi.seguimiento(c.cuentaId, c.documentoId, { estado: 'revisando' });
      cargar(true);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo abrir la cuenta' });
    } finally {
      setOcupado(null);
    }
  };

  return (
    <SuperadminLayout>
      <Encabezado titulo="Rechazos de Hacienda"
        subtitulo="Comprobantes rechazados, con error o sin respuesta de todas las cuentas (último año). Cuando el comprobante queda aceptado, el caso sale solo.">
        <label className="flex items-center gap-2 text-sm text-gray-600 mr-2">
          <input type="checkbox" checked={verCerrados} onChange={e => setVerCerrados(e.target.checked)} />Incluir resueltos y descartados
        </label>
        <Boton icono={RefreshCw} cargando={refrescando} onClick={() => cargar(true)}>Actualizar</Boton>
      </Encabezado>
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />

      {inicio && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <span>Se muestran comprobantes desde el <strong className="text-gray-900">{fechaCorta(inicio)}</strong> (inicio de operaciones; lo anterior fueron pruebas).</span>
          {editInicio === null ? (
            <button onClick={() => setEditInicio(inicio)} className="text-blue-600 hover:underline">Cambiar</button>
          ) : (
            <span className="inline-flex items-center gap-2">
              <input type="date" value={editInicio} onChange={e => setEditInicio(e.target.value)} className="px-2 py-1 border border-gray-300 rounded-lg text-sm" />
              <Boton variante="primario" onClick={guardarInicio} disabled={!editInicio}>Guardar</Boton>
              <Boton onClick={() => setEditInicio(null)}>Cancelar</Boton>
            </span>
          )}
        </div>
      )}

      {casos.filter(c => c.errorLectura).map(c => (
        <Aviso key={c.cuentaId} aviso={{ tipo: 'error', texto: `No se pudo leer la cuenta ${c.cuenta}: ${c.errorLectura}` }} />
      ))}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Kpi icono={AlertTriangle} color="red" titulo="Nuevos (sin atender)" valor={cuenta_('nuevo')} />
        <Kpi icono={Wrench} color="blue" titulo="En revisión" valor={cuenta_('revisando')} />
        <Kpi icono={UserCheck} color="amber" titulo="Requieren soporte" valor={deSoporte} detalle="Corrección de fondo, no la hace el cliente" />
        <Kpi icono={CheckCircle2} color="green" titulo="Cerrados" valor={verCerrados ? cuenta_('resuelto') + cuenta_('descartado') : '—'} detalle={verCerrados ? null : 'Active "Incluir resueltos"'} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex flex-col xl:flex-row gap-3 p-4 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar por consecutivo, cliente, empresa o mensaje…" className={clsx(inputCls, 'pl-9')} />
          </div>
          <select value={cuenta} onChange={e => setCuenta(e.target.value)} className={clsx(inputCls, 'xl:w-56')}>
            <option value="">Todas las cuentas</option>
            {cuentas.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
          <select value={categoria} onChange={e => setCategoria(e.target.value)} className={clsx(inputCls, 'xl:w-64')}>
            <option value="">Todos los motivos</option>
            {categorias.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
          <select value={estado} onChange={e => setEstado(e.target.value)} className={clsx(inputCls, 'xl:w-44')}>
            <option value="">Todo seguimiento</option>
            {Object.entries(SEGUIMIENTO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {cargando ? <Cargando texto="Revisando los comprobantes de cada cuenta…" /> : lista.length === 0 ? (
          <Vacio icono={CheckCircle2} titulo="No hay casos" texto={validos.length ? 'Ningún caso coincide con el filtro.' : 'No hay comprobantes rechazados ni trabados. Todo aceptado por Hacienda.'} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {lista.map(c => {
              const k = `${c.cuentaId}:${c.documentoId}`;
              const s = SEGUIMIENTO[c.seguimiento.estado];
              const exp = abierto === k;
              return (
                <li key={k} className="px-5 py-4">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium border', s.cls)}>{s.label}</span>
                        <span className={clsx('text-xs font-medium', c.estadoHacienda === 'rechazado' ? 'text-red-700' : 'text-amber-700')}>{HACIENDA[c.estadoHacienda] || c.estadoHacienda}</span>
                        {c.pruebas && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">ambiente de pruebas</span>}
                        <span className={clsx('text-xs px-1.5 py-0.5 rounded', c.categoria.quien === 'soporte' ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-700')}>
                          {c.categoria.quien === 'soporte' ? 'Lo corrige soporte' : 'Lo puede corregir el cliente'}
                        </span>
                      </div>
                      <p className="mt-1.5 font-semibold text-gray-900">{c.categoria.titulo}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-mono text-xs">{c.tipo} {c.consecutivo || `#${c.documentoId}`}</span>
                        {' · '}{c.empresa} <span className="text-gray-400">({c.cuenta})</span>
                        {c.cliente ? <> · cliente {c.cliente}</> : null}
                        {' · '}{fMonto(c.total, c.moneda)} · {fFechaHora(c.fecha)}
                      </p>
                      {c.mensaje && <p className="mt-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 line-clamp-2">{c.mensaje}</p>}
                      {(c.seguimiento.nota || c.seguimiento.avisadoAt) && (
                        <p className="mt-2 text-xs text-gray-500">
                          {c.seguimiento.avisadoAt && <span className="mr-3"><BellRing className="inline h-3 w-3 mr-1" />Cliente avisado {hace(c.seguimiento.avisadoAt)}{c.seguimiento.avisadoPor ? ` por ${c.seguimiento.avisadoPor}` : ''}</span>}
                          {c.seguimiento.nota && <span><MessageSquare className="inline h-3 w-3 mr-1" />{c.seguimiento.nota}</span>}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap lg:flex-col gap-2 lg:w-52 shrink-0">
                      <Boton variante="azul" icono={LogIn} className="lg:w-full" cargando={ocupado === `entrar:${k}`} onClick={() => entrar(c)}>Abrir la cuenta</Boton>
                      {c.seguimiento.estado !== 'resuelto' && <Boton variante="verde" icono={CheckCircle2} className="lg:w-full" cargando={ocupado === k} onClick={() => actualizar(c, { estado: 'resuelto' }, 'Caso marcado como resuelto')}>Marcar resuelto</Boton>}
                      <button onClick={() => setAbierto(exp ? null : k)} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 justify-center">
                        {exp ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}{exp ? 'Menos' : 'Cómo corregirlo y más'}
                      </button>
                    </div>
                  </div>

                  {exp && (
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-lg border border-gray-200 p-4 text-sm">
                        <p className="font-medium text-gray-900 mb-1">Por qué pasa</p>
                        <p className="text-gray-600">{c.categoria.causa}</p>
                        <p className="font-medium text-gray-900 mt-3 mb-1">Cómo se corrige</p>
                        <p className="text-gray-600">{c.categoria.correccion}</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 p-4 text-sm space-y-3">
                        <p className="font-medium text-gray-900">Seguimiento</p>
                        <div className="flex flex-wrap gap-2">
                          <Boton icono={Wrench} disabled={c.seguimiento.estado === 'revisando'} onClick={() => actualizar(c, { estado: 'revisando' }, 'Caso en revisión')}>En revisión</Boton>
                          <Boton icono={BellRing} onClick={() => actualizar(c, { avisado: true }, 'Quedó registrado que se avisó al cliente')}>Cliente avisado</Boton>
                          <Boton icono={MessageSquare} onClick={() => { setNota(c); setTextoNota(c.seguimiento.nota || ''); }}>Nota</Boton>
                          <Boton icono={XCircle} onClick={() => actualizar(c, { estado: 'descartado' }, 'Caso descartado')}>Descartar</Boton>
                          {['resuelto', 'descartado'].includes(c.seguimiento.estado) && <Boton onClick={() => actualizar(c, { estado: 'nuevo' }, 'Caso reabierto')}>Reabrir</Boton>}
                          <Boton icono={Eye} onClick={() => setMensaje(c)}>Mensaje completo</Boton>
                        </div>
                        {c.seguimiento.actualizadoPor && <p className="text-xs text-gray-500">Última actualización por {c.seguimiento.actualizadoPor} {hace(c.seguimiento.actualizadoAt)}</p>}
                        {c.clave && <p className="text-xs text-gray-500 break-all">Clave: <span className="font-mono">{c.clave}</span></p>}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {nota && (
        <Modal titulo="Nota del caso" icono={MessageSquare} onClose={() => setNota(null)} ancho="max-w-xl"
          pie={<>
            <Boton onClick={() => setNota(null)}>Cancelar</Boton>
            <Boton variante="primario" onClick={async () => { const c = nota; setNota(null); await actualizar(c, { nota: textoNota }, 'Nota guardada'); }}>Guardar</Boton>
          </>}>
          <p className="text-sm text-gray-600 mb-3">{nota.tipo} {nota.consecutivo} · {nota.empresa}</p>
          <Campo label="Qué se hizo o qué falta"><textarea autoFocus rows={4} className={inputCls} value={textoNota} onChange={e => setTextoNota(e.target.value)} /></Campo>
        </Modal>
      )}

      {mensaje && (
        <Modal titulo="Respuesta de Hacienda" icono={Eye} onClose={() => setMensaje(null)} pie={<Boton onClick={() => setMensaje(null)}>Cerrar</Boton>}>
          <p className="text-sm text-gray-600 mb-3">{mensaje.tipo} {mensaje.consecutivo} · {mensaje.empresa} · {HACIENDA[mensaje.estadoHacienda]}</p>
          <pre className="text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">{mensaje.mensajeCompleto || 'Hacienda no devolvió mensaje (sin respuesta).'}</pre>
        </Modal>
      )}
    </SuperadminLayout>
  );
}
